import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { MfaChallenge } from '../MFA';

// Separate component that uses reCAPTCHA hook
const LoginForm = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [lockoutUntil, setLockoutUntil] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [mfaPending, setMfaPending] = useState(null);  // { mfaToken, availableFactors } | null
  const { login } = useAuth();
  const { executeRecaptcha } = useGoogleReCaptcha();

  // Check for existing lockout on mount
  useEffect(() => {
    const storedLockout = localStorage.getItem('loginLockoutUntil');
    if (storedLockout) {
      const lockoutTime = parseInt(storedLockout, 10);
      if (lockoutTime > Date.now()) {
        setLockoutUntil(lockoutTime);
        const remainingSeconds = Math.ceil((lockoutTime - Date.now()) / 1000);
        setCountdown(remainingSeconds);
        setError(`Too many attempts. Please try again later.`);
      } else {
        localStorage.removeItem('loginLockoutUntil');
      }
    }
  }, []);

  // Countdown timer effect
  useEffect(() => {
    if (!lockoutUntil) return;

    const timer = setInterval(() => {
      const remaining = Math.ceil((lockoutUntil - Date.now()) / 1000);
      if (remaining <= 0) {
        setLockoutUntil(null);
        setCountdown(null);
        setError('');
        localStorage.removeItem('loginLockoutUntil');
        clearInterval(timer);
      } else {
        setCountdown(remaining);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [lockoutUntil]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (lockoutUntil) return;

    setLoading(true);
    setError('');

    try {
      // Execute invisible reCAPTCHA (if available and not disabled in test env)
      let captchaToken = null;
      const skipRecaptcha = process.env.REACT_APP_DISABLE_RECAPTCHA === 'true';

      if (executeRecaptcha && !skipRecaptcha) {
        try {
          captchaToken = await executeRecaptcha('login');
        } catch (captchaError) {
          console.warn('reCAPTCHA execution failed:', captchaError);
          // Continue without captcha if it fails (server will validate based on RECAPTCHA_ENABLED)
        }
      }

      const result = await login(email, password, captchaToken);

      if (result.mfaRequired) {
        setMfaPending({
          mfaToken: result.mfaToken,
          availableFactors: result.availableFactors,
          userRole: result.userRole || '',
        });
        setLoading(false);
        return;
      }

      if (!result.success) {
        // Check for rate limit error
        if (result.error && result.error.includes("Rate limit")) {
          const lockoutTime = Date.now() + 15 * 60 * 1000;
          setLockoutUntil(lockoutTime);
          setCountdown(15 * 60);
          localStorage.setItem('loginLockoutUntil', lockoutTime.toString());
          setError(`Too many attempts. Please try again in 15:00`);
        } else if (result.error && result.error.includes("CAPTCHA")) {
          setError('Security verification failed. Please try again.');
        } else {
          setError(result.error);
        }
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    }
    
    setLoading(false);
  }, [email, password, lockoutUntil, login, executeRecaptcha]);

  return (
    <div className="min-h-screen w-full flex items-center justify-center relative overflow-hidden bg-gradient-to-tl from-blue-900 to-black">
      {/* Background Animation Layer */}
      <div className="absolute inset-0 z-0" />

      {/* Content Layer */}
      <div 
        className="w-full max-w-md p-8 m-4 transform transition-all duration-300 relative z-10"
        style={{
          background: 'rgba(70, 68, 68, 0.14)',
          borderRadius: '16px',
          boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
          backdropFilter: 'blur(5.5px)',
          WebkitBackdropFilter: 'blur(5.5px)',
          border: '1px solid rgba(255, 255, 255, 0.3)'
        }}
      >
        <div>
          <h2 className="text-center text-3xl font-bold text-gray-50 drop-shadow-sm">
            Matrica Docutech Pvt. Ltd.
          </h2>
          <p className="mt-2 text-center text-sm text-white">
            Order Management System
          </p>
        </div>

        {mfaPending ? (
          <div className="mt-8">
            <MfaChallenge
              mfaToken={mfaPending.mfaToken}
              availableFactors={mfaPending.availableFactors}
              userRole={mfaPending.userRole}
              onCancel={() => {
                setMfaPending(null);
                setError('');
              }}
              onError={(msg) => {
                setMfaPending(null);
                setError(msg);
              }}
            />
          </div>
        ) : (
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-200 shadow-sm">
              Email Address
            </label>
            <input
              id="email"
              name="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={!!lockoutUntil}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 transition duration-200 disabled:opacity-50 bg-white bg-opacity-90 text-gray-900"
              autoComplete="username"
              placeholder="Enter your email"
            />
          </div>
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-200 shadow-sm">
              Password
            </label>
            <div className="relative">
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={!!lockoutUntil}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 transition duration-200 pr-10 disabled:opacity-50 bg-white bg-opacity-90"
                autoComplete="current-password"
                placeholder="Enter your password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                disabled={!!lockoutUntil}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700 disabled:opacity-50"
              >
                {showPassword ? (
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
          </div>
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative text-sm" role="alert">
              <span className="block sm:inline">{error}</span>
            </div>
          )}
          <button
            type="submit"
            disabled={loading || !!lockoutUntil}
            className={`w-full flex justify-center py-2.5 px-4 border border-transparent rounded-md shadow-lg text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-400 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200 transform ${email && password ? 'bg-green-500 hover:scale-[1.01]' : 'bg-gray-400'}`}
          >
            {loading ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Signing in...
              </span>
            ) : lockoutUntil ? (
              `Try again in ${formatTime(countdown)}`
            ) : (
              'Sign In'
            )}
          </button>
          <div className="text-center mt-2">
            <a
              href="/forgot-password"
              className="text-sm text-white hover:text-gray-200 transition-colors"
              style={{ opacity: 0.8 }}
            >
              Forgot your password?
            </a>
          </div>
        </form>
        )}
      </div>
    </div>
  );
};

// Main Login component with reCAPTCHA provider
const Login = () => {
  const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  const skipRecaptcha = process.env.REACT_APP_DISABLE_RECAPTCHA === 'true';

  // Skip provider entirely if disabled (test env) or no sitekey configured
  if (!siteKey || skipRecaptcha) {
    return <LoginForm />;
  }

  return (
    <GoogleReCaptchaProvider reCaptchaKey={siteKey}>
      <LoginForm />
    </GoogleReCaptchaProvider>
  );
};

export default Login;